import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import { Gift, Calendar, MapPin, Link2, Users, ArrowLeft } from 'lucide-react';

const OPPORTUNITY_TYPES = [
  { value: 'job_fair', label: 'Job Fair' },
  { value: 'job_posting', label: 'Job Posting' },
  { value: 'food_distribution', label: 'Food Distribution' },
  { value: 'clothing_drive', label: 'Clothing Drive' },
  { value: 'housing', label: 'Housing Opportunity' },
  { value: 'training', label: 'Training Program' },
  { value: 'recovery_meeting', label: 'Recovery Meeting' },
  { value: 'transportation', label: 'Transportation Assistance' },
  { value: 'community_event', label: 'Community Event' },
  { value: 'scholarship', label: 'Scholarship Opportunity' },
  { value: 'volunteer', label: 'Volunteer Opportunity' },
];

export default function PostOpportunityPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    opportunity_type: 'community_event',
    description: '',
    date: '',
    time: '',
    location: '',
    eligibility: '',
    contact_info: '',
    application_link: '',
    capacity: '',
    is_recurring: false,
    recurrence_details: ''
  });
  const api = axiosAuth();

  const handleSubmit = async () => {
    if (!form.title) {
      toast.error('Title is required');
      return;
    }
    if (!form.description) {
      toast.error('Description is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        capacity: form.capacity ? parseInt(form.capacity) : null
      };
      await api.post('/opportunities', payload);
      toast.success('Opportunity posted successfully!');
      navigate('/my-opportunities');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post opportunity');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="post-opportunity-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Post New Opportunity</h1>
          <p className="text-muted-foreground text-sm mt-1">Share opportunities with the community</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Gift className="w-4 h-4 text-emerald-600" />
                Opportunity Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Title *</Label>
                <Input 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="e.g., Job Fair for Second Chances"
                  data-testid="opp-title-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Opportunity Type *</Label>
                <Select value={form.opportunity_type} onValueChange={v => setForm({...form, opportunity_type: v})}>
                  <SelectTrigger data-testid="opp-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OPPORTUNITY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Description *</Label>
                <DictationInput 
                  value={form.description} 
                  onChange={(val) => setForm({...form, description: val})}
                  placeholder="Provide details about this opportunity..."
                  rows={4}
                  data-testid="opp-description-input"
                showCleanup={false} />
              </div>
              <div className="space-y-1.5">
                <Label>Eligibility Requirements</Label>
                <Input 
                  value={form.eligibility} 
                  onChange={e => setForm({...form, eligibility: e.target.value})}
                  placeholder="Who is eligible for this opportunity?"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                Date & Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={form.is_recurring} 
                    onCheckedChange={v => setForm({...form, is_recurring: v})}
                    data-testid="opp-recurring-switch"
                  />
                  <Label className="cursor-pointer">Recurring Event</Label>
                </div>
              </div>
              
              {form.is_recurring ? (
                <div className="space-y-1.5">
                  <Label>Recurrence Details</Label>
                  <Input 
                    value={form.recurrence_details} 
                    onChange={e => setForm({...form, recurrence_details: e.target.value})}
                    placeholder="e.g., Every Thursday, First Monday of each month"
                  />
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input 
                      type="date"
                      value={form.date} 
                      onChange={e => setForm({...form, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Time</Label>
                    <Input 
                      value={form.time} 
                      onChange={e => setForm({...form, time: e.target.value})}
                      placeholder="e.g., 10:00 AM - 2:00 PM"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><MapPin size={14} /> Location</Label>
                <Input 
                  value={form.location} 
                  onChange={e => setForm({...form, location: e.target.value})}
                  placeholder="Address or location description"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Link2 className="w-4 h-4 text-emerald-600" />
                Additional Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Contact Information</Label>
                <Input 
                  value={form.contact_info} 
                  onChange={e => setForm({...form, contact_info: e.target.value})}
                  placeholder="Phone or email for inquiries"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Application / Registration Link</Label>
                <Input 
                  value={form.application_link} 
                  onChange={e => setForm({...form, application_link: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Users size={14} /> Capacity (optional)</Label>
                <Input 
                  type="number"
                  value={form.capacity} 
                  onChange={e => setForm({...form, capacity: e.target.value})}
                  placeholder="Maximum number of participants"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
              data-testid="submit-opp-button"
            >
              <Gift size={16} />
              {saving ? 'Posting...' : 'Post Opportunity'}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div>
          <Card className="border-0 shadow-sm rounded-2xl sticky top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                    <Gift className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{form.title || 'Opportunity Title'}</p>
                    <p className="text-xs text-emerald-600 capitalize">{form.opportunity_type.replace('_', ' ')}</p>
                  </div>
                </div>
                {form.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3">{form.description}</p>
                )}
                <div className="text-xs space-y-1 text-muted-foreground">
                  {form.is_recurring ? (
                    form.recurrence_details && <p>Schedule: {form.recurrence_details}</p>
                  ) : (
                    form.date && <p>Date: {form.date} {form.time}</p>
                  )}
                  {form.location && <p>Location: {form.location}</p>}
                  {form.capacity && <p>Capacity: {form.capacity} spots</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
